import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import type {
  LiveCostingDetail,
  QuarantinedPrice,
} from "@shared-core/data/cost-engine-validation-types";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../primitives/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../primitives/alert-dialog";
import { Textarea } from "../../primitives/textarea";
import { Check, Loader2, X } from "lucide-react";

/**
 * The item page's live-costing checks panel (product brief §8, journey 2;
 * review decisions in `02_glossary_and_live_costing.md`): the checks that
 * measure the cost engine against Tharstern for this item. There is nothing
 * to pause or resume — in-house prices come from Tharstern estimates — so the
 * panel only reports the checks and releases quarantined prices.
 *
 * **Quarantined prices** (spec 03 §5.1 step 4c): a rolling price whose
 * Tharstern estimate request timed out is taken out of the weekly rotation
 * until someone releases it here, with a note — a retry is what hung
 * Tharstern for everyone. Each row says why, in the check's own words.
 */
export function LiveCostingPanel({
  detail,
  derivedItemCount,
  onReleaseQuarantine,
}: {
  detail: LiveCostingDetail;
  /** Catalogue items derived from this dictionary item — drives the sub-line. */
  derivedItemCount: number;
  /** Release one quarantined price back into the weekly rotation. */
  onReleaseQuarantine: (price: QuarantinedPrice, note: string) => Promise<void>;
}) {
  const [releasing, setReleasing] = useState<QuarantinedPrice | null>(null);
  const [releaseNote, setReleaseNote] = useState("");
  const [releaseSubmitting, setReleaseSubmitting] = useState(false);

  async function submitRelease() {
    if (!releasing) return;
    setReleaseSubmitting(true);
    try {
      await onReleaseQuarantine(releasing, releaseNote);
      toast.success("Price released — the next run can check it again");
      setReleasing(null);
      setReleaseNote("");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to release the price",
      );
    } finally {
      setReleaseSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Live costing checks</CardTitle>
        <p className="text-xs text-muted-foreground">
          How the cost engine compares with Tharstern for this dictionary item,
          which {derivedItemCount} catalogue{" "}
          {derivedItemCount === 1 ? "item derives" : "items derive"} from.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="space-y-1.5 text-sm">
          {detail.checklist.map((item) => (
            <li key={item.key} className="flex items-start gap-2">
              {item.met ? (
                <Check className="mt-0.5 size-3.5 shrink-0 text-[color:var(--color-success)]" />
              ) : (
                <X className="mt-0.5 size-3.5 shrink-0 text-destructive" />
              )}
              <span className={item.met ? "" : "font-medium"}>
                {item.label}
                {item.key === "no_open_red" && !item.met && (
                  <>
                    {" — "}
                    <Link
                      to={paths.settingsLiveCostingChecks({
                        view: "cost-line",
                      })}
                      className="font-normal text-[color:var(--color-primary)] hover:underline"
                    >
                      see the by-cost-line view in live costing checks
                    </Link>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>

        {detail.quarantinedPrices.length > 0 && (
          <div className="border-t border-border pt-3">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Quarantined prices ({detail.quarantinedPrices.length})
            </p>
            <p className="mb-2 text-xs text-muted-foreground">
              Tharstern timed out on these, so no rolling run checks them until
              you release them.
            </p>
            <ul className="space-y-2 text-sm">
              {detail.quarantinedPrices.map((price) => (
                <li
                  key={price.rollingPriceId}
                  className="flex items-start justify-between gap-3"
                >
                  <div className="min-w-0 space-y-0.5">
                    <p className="font-medium">
                      {price.variantLabel} · {price.quantity.toLocaleString()}
                    </p>
                    {price.fixedCostOptions.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {price.fixedCostOptions.map((option) => (
                          <Badge
                            key={`${option.fieldKey}:${option.optionCode}`}
                            variant="outline"
                          >
                            {option.label}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {price.quarantineReason} · quarantined{" "}
                      {formatDateTimeOrDash(price.quarantinedAt)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label={`Release ${price.variantLabel} at ${price.quantity}`}
                    onClick={() => setReleasing(price)}
                  >
                    Release
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>

      <AlertDialog
        open={releasing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setReleasing(null);
            setReleaseNote("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Release this price?</AlertDialogTitle>
            <AlertDialogDescription>
              {releasing
                ? `${releasing.variantLabel} at ${releasing.quantity.toLocaleString()} goes back into the weekly checks. If Tharstern still hangs on it, every estimate request after it times out until Tharstern recovers.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Note (required) — why can Tharstern price this now?"
            value={releaseNote}
            onChange={(e) => setReleaseNote(e.target.value)}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!releaseNote.trim() || releaseSubmitting}
              onClick={submitRelease}
            >
              {releaseSubmitting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Release"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
